import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";

// Tiny in-house toast system — matches Soft Modern: rounded, soft shadow,
// pastel-tinted by severity. Auto-dismiss 4s; sticky option for important
// errors that the user must acknowledge.

export type ToastSeverity = "success" | "error" | "warning" | "info";

interface ToastItem {
  id: number;
  severity: ToastSeverity;
  message: string;
  durationMs: number;
  sticky: boolean;
}

let counter = 1;
let items: ToastItem[] = [];
const subscribers = new Set<(items: ToastItem[]) => void>();

function emit() {
  subscribers.forEach((s) => s([...items]));
}

function push(
  severity: ToastSeverity,
  message: string,
  opts?: { durationMs?: number; sticky?: boolean },
) {
  const sticky = opts?.sticky ?? false;
  const durationMs = opts?.durationMs ?? 4000;
  const item: ToastItem = { id: counter++, severity, message, durationMs, sticky };
  items = [...items, item];
  emit();
  if (!sticky) {
    setTimeout(() => {
      items = items.filter((i) => i.id !== item.id);
      emit();
    }, durationMs);
  }
}

function dismiss(id: number) {
  items = items.filter((i) => i.id !== id);
  emit();
}

export const toast = {
  success: (message: string, opts?: { durationMs?: number; sticky?: boolean }) =>
    push("success", message, opts),
  error: (message: string, opts?: { durationMs?: number; sticky?: boolean }) =>
    push("error", message, opts),
  warning: (message: string, opts?: { durationMs?: number; sticky?: boolean }) =>
    push("warning", message, opts),
  info: (message: string, opts?: { durationMs?: number; sticky?: boolean }) =>
    push("info", message, opts),
};

export function Toaster() {
  const [list, setList] = useState<ToastItem[]>([]);

  useEffect(() => {
    subscribers.add(setList);
    return () => {
      subscribers.delete(setList);
    };
  }, []);

  return (
    <div
      aria-live="polite"
      className="pointer-events-none fixed top-6 right-6 z-50 flex w-[380px] flex-col gap-3"
    >
      {list.map((t) => (
        <ToastView key={t.id} item={t} onDismiss={() => dismiss(t.id)} />
      ))}
    </div>
  );
}

function ToastView({
  item,
  onDismiss,
}: {
  item: ToastItem;
  onDismiss: () => void;
}) {
  const palette = {
    success: { bg: "bg-sage-soft", dot: "bg-sage", text: "text-ink" },
    error: { bg: "bg-danger-soft", dot: "bg-danger", text: "text-ink" },
    warning: { bg: "bg-butter-soft", dot: "bg-butter-ink", text: "text-ink" },
    info: { bg: "bg-sky-soft", dot: "bg-sky-ink", text: "text-ink" },
  }[item.severity];

  return (
    <div
      role="status"
      className={cn(
        "pointer-events-auto relative rounded-lg shadow-lift",
        "bg-surface border border-rule",
        "animate-reveal",
      )}
    >
      <div className={cn("flex items-start gap-3 rounded-lg p-4", palette.bg)}>
        <span
          aria-hidden
          className={cn(
            "mt-1.5 inline-flex h-2 w-2 shrink-0 rounded-full",
            palette.dot,
          )}
        />
        <div className={cn("flex-1 text-[0.92rem] leading-snug", palette.text)}>
          {item.message}
        </div>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="ml-2 text-[1.2rem] leading-none text-ink-faint hover:text-ink"
        >
          ×
        </button>
      </div>
    </div>
  );
}
